import type { ItemDTO } from "@barkup/shared";

const UNITS = ["B", "KB", "MB", "GB"];

/** Shared byte formatter — used anywhere quota/file sizes are shown (App.vue, ItemCard, PlansView). */
export function formatBytes(v: string | number): string {
  let value = Number(v);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${UNITS[unitIndex]}`;
}

/**
 * Who to credit an item to.
 *
 * The two null cases are NOT the same thing and must not read the same way:
 * account deletion clears senderId *and* senderDisplayName together
 * (routes/liff/account.ts), so a surviving senderId with no name just means
 * LINE declined the profile lookup at ingest time — a blocked OA, a transient
 * API error. Calling that person "ผู้ใช้ที่ลบบัญชีแล้ว" claims something about
 * them that is simply untrue, on a line the whole group can read.
 */
export function senderLabel(item: ItemDTO): string {
  if (item.senderDisplayName) return item.senderDisplayName;
  return item.senderId ? "[ไม่ทราบชื่อผู้ส่ง]" : "[ผู้ใช้ที่ลบบัญชีแล้ว]";
}

/**
 * What to call an item in a list row, tile, or aria-label.
 *
 * Falls through on falsiness, not just null: an empty-string `filename` is a
 * real thing to receive (a LINE file message can arrive with a blank name, and
 * `ogTitle` is whatever a scraped page put in its `<title>`), and `??` would
 * accept it — rendering a nameless row with a blank accessible name, which is
 * unreachable by screen reader and untappable-looking to everyone else.
 */
export function itemTitle(item: ItemDTO): string {
  return item.filename || item.ogTitle || item.linkUrl || "(ไม่มีชื่อ)";
}

/**
 * Local-day boundaries for an `<input type="date">` value.
 *
 * `new Date("2026-07-22")` is parsed as UTC midnight, so sending it straight to
 * the server meant "ถึงวันที่ = today" resolved to 07:00 this morning in
 * Bangkok and silently hid everything sent since — the filter under-reported by
 * most of a day, and an empty result looked like an empty archive. The date the
 * user picked is a local calendar day; these turn it into the instant range
 * that day actually covers on their device.
 */
export function dayStartISO(value: string): string | undefined {
  const parts = parseDateInput(value);
  return parts && new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0).toISOString();
}

export function dayEndISO(value: string): string | undefined {
  const parts = parseDateInput(value);
  return parts && new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999).toISOString();
}

/**
 * A date input can hold a partial or nonsensical value — desktop browsers let
 * the year field be typed into freely ("0002-07-22"), and a cleared field is
 * "". Anything that isn't a real calendar date returns undefined, which drops
 * the filter, rather than an Invalid Date whose `.toISOString()` throws and
 * takes the whole list request with it.
 */
function parseDateInput(value: string): [number, number, number] | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) return undefined;
  return [year, month, day];
}

/** Compact date for a list row's metadata line — "14 ก.ค." this year, "14 ก.ค. 68" otherwise. */
export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "2-digit" }) });
}

/**
 * Date-group bucket for the archive list (Drive/Photos-style scanning aid).
 * Buckets are coarse on purpose — "วันนี้ / เมื่อวาน / สัปดาห์นี้ / เดือนนี้ /
 * <เดือน ปี>" — headers should orient, not fragment the list into tiny groups.
 */
export function dateGroupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const dayStart = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const DAY_MS = 86_400_000;
  const today = dayStart(now);
  const that = dayStart(d);
  if (that === today) return "วันนี้";
  if (that === today - DAY_MS) return "เมื่อวาน";
  if (that > today - 7 * DAY_MS) return "สัปดาห์นี้";
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return "เดือนนี้";
  return d.toLocaleDateString("th-TH", { month: "long", year: "numeric" });
}

/**
 * Buckets an already-sorted (createdAt desc) item list into consecutive
 * date groups for section headers. Consecutive-run grouping (not a map) so
 * the server's sort order is preserved exactly.
 */
export function groupItemsByDate(
  items: ItemDTO[],
  dateOf: (item: ItemDTO) => string = (item) => item.createdAt
): { label: string; items: ItemDTO[] }[] {
  const groups: { label: string; items: ItemDTO[] }[] = [];
  for (const item of items) {
    const label = dateGroupLabel(dateOf(item));
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}
